import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { t } from '@/i18n';

const GlassTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/90 backdrop-blur-md border border-border rounded-xl shadow-lg px-4 py-3">
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-bold" style={{ color: p.color, fontVariantNumeric: 'tabular-nums' }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

export const KpiDrilldownDialog = ({ open, onOpenChange, title, kpiKey, dashboardData }) => {
  const hourly = useMemo(() => dashboardData?.productionHourly || [], [dashboardData?.productionHourly]);
  const weekly = useMemo(() => dashboardData?.productionWeek || [], [dashboardData?.productionWeek]);
  const kpi = dashboardData?.kpis?.[kpiKey];
  const unit = kpi?.unit || '';

  // Deterministic monthly data (no Math.random)
  const monthly = useMemo(() => {
    const weeks = [t('drilldown.week1'), t('drilldown.week2'), t('drilldown.week3'), t('drilldown.week4')];
    return weeks.map((w, i) => {
      const base = weekly.reduce((s, d) => s + (d.actual || 0), 0);
      const factor = 0.9 + ((i * 37 + 13) % 20) / 100;
      return { period: w, value: Math.round(base * factor), target: weekly[0]?.target ? weekly.reduce((s, d) => s + (d.target || 0), 0) : undefined };
    });
  }, [weekly]);

  // Dynamic peak hour & best day
  const peakHour = useMemo(() =>
    hourly.length > 0 ? hourly.reduce((max, h) => h.output > max.output ? h : max, hourly[0]) : null,
  [hourly]);

  const bestDay = useMemo(() =>
    weekly.length > 0 ? weekly.reduce((max, d) => d.actual > max.actual ? d : max, weekly[0]) : null,
  [weekly]);

  if (!dashboardData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl bg-white/95 backdrop-blur-md">
        <DialogHeader>
          <DialogTitle>{title} — {t('drilldown.title')}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="today" className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="today">{t('drilldown.todayHourly')}</TabsTrigger>
            <TabsTrigger value="week">{t('drilldown.thisWeek')}</TabsTrigger>
            <TabsTrigger value="month">{t('drilldown.thisMonth')}</TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="mt-4">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={hourly}>
                <defs>
                  <linearGradient id="drillGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                <XAxis dataKey="hour" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Area type="monotone" dataKey="output" stroke="hsl(var(--chart-1))" strokeWidth={2} fill="url(#drillGrad)" name={`${t('drilldown.total')} (${unit || 't'})`} />
              </AreaChart>
            </ResponsiveContainer>

            <div className="mt-3 grid grid-cols-3 gap-3 text-center">
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.peakHour')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{peakHour?.hour} — {peakHour?.output}t</div>
              </div>
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.avgPerHour')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(hourly.filter(h => h.output > 0).reduce((s, h) => s + h.output, 0) / Math.max(hourly.filter(h => h.output > 0).length, 1))}t</div>
              </div>
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.total')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{hourly.reduce((s, h) => s + h.output, 0).toLocaleString()}t</div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="week" className="mt-4">
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={weekly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                <XAxis dataKey="day" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Bar dataKey="actual" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} name={t('dashboard.actual')} opacity={0.8} />
                <Line type="monotone" dataKey="target" stroke="hsl(23, 81%, 52%)" strokeWidth={2} strokeDasharray="6 3" dot={false} name={t('dashboard.target')} />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="mt-3 grid grid-cols-3 gap-3 text-center">
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.bestDay')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{bestDay?.day} — {bestDay?.actual?.toLocaleString()}t</div>
              </div>
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.weeklyAvg')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(weekly.reduce((s, d) => s + d.actual, 0) / weekly.length).toLocaleString()}t</div>
              </div>
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.vsTarget')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round((weekly.reduce((s, d) => s + d.actual, 0) / weekly.reduce((s, d) => s + d.target, 0)) * 100)}%</div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="month" className="mt-4">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.5} />
                <XAxis dataKey="period" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip content={<GlassTooltip />} />
                <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[6, 6, 0, 0]} name={t('drilldown.total')} opacity={0.8} />
                {monthly[0]?.target && (
                  <ReferenceLine y={monthly[0].target} stroke="hsl(23, 81%, 52%)" strokeDasharray="6 3" label={{ value: t('dashboard.target'), position: 'right', fill: 'hsl(23, 81%, 52%)', fontSize: 11 }} />
                )}
              </BarChart>
            </ResponsiveContainer>

            <div className="mt-3 grid grid-cols-2 gap-3 text-center">
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.monthTotal')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{monthly.reduce((s, d) => s + d.value, 0).toLocaleString()}t</div>
              </div>
              <div className="bg-muted/40 border border-border rounded-lg p-3">
                <div className="text-xs text-muted-foreground">{t('drilldown.weeklyAvg')}</div>
                <div className="text-base font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(monthly.reduce((s, d) => s + d.value, 0) / 4).toLocaleString()}t</div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
